/**
 * CS-5A — THE password rules the portal shows, in one place (technical-foundation §14.2 as amended 2026-09-28).
 *
 * Mirrors the server's `utils/passwordPolicy.js` + `utils/commonPasswords.js`. The server stays the authority and
 * its message wins on any disagreement; this exists so the customer sees each rule met or not met while typing.
 * Used by activation, reset, the forced first change and the voluntary change on Security — never re-written
 * per page.
 */

const COMMON_BASE_WORDS = [
  "password", "passw", "passwd", "pass", "welcome", "qwerty", "qwertyuiop", "asdf", "asdfgh", "zxcvbn",
  "admin", "administrator", "letmein", "iloveyou", "monkey", "dragon", "sunshine", "princess", "football",
  "baseball", "cricket", "master", "shadow", "superman", "batman", "trustno", "abc", "abcd", "abcdef",
  "login", "user", "test", "guest", "secret", "hello", "freedom", "whatever", "starwars", "charlie",
  "michael", "jesus", "god", "love", "lovely", "india", "bharat", "hyderabad", "vijayawada", "krishna",
  "telangana", "andhra", "vvr", "vvrportal", "portal", "customer", "changeme", "default", "temp",
  "temporary", "newpassword", "mypassword", "summer", "winter", "spring", "autumn", "monday", "january",
  "computer", "internet", "samsung", "google", "apple", "facebook", "flower", "angel", "baby", "family",
];
const RUNS = ["qwerty", "asdfgh", "zxcvbn", "abcdef", "123456", "654321", "1q2w3e", "qazwsx"];

export const MIN_LENGTH = 8;

export function isCommonPassword(pw, email = "") {
  const s = String(pw || "");
  const lower = s.toLowerCase();
  const base = lower.replace(/[^a-z]/g, "");
  if (!base) return true;
  if (COMMON_BASE_WORDS.includes(base)) return true;
  if (RUNS.some((run) => lower.includes(run))) return true;
  if (/(.)\1{3,}/.test(lower)) return true;
  const local = String(email || "").toLowerCase().split("@")[0].replace(/[^a-z]/g, "");
  if (local.length >= 4 && base.includes(local)) return true;
  return false;
}

/** Each rule, in display order: `{ key, label, met }`. */
export function passwordChecks(pw, { email } = {}) {
  const s = String(pw || "");
  return [
    { key: "length", label: `At least ${MIN_LENGTH} characters`, met: s.length >= MIN_LENGTH },
    { key: "upper", label: "An uppercase letter", met: /[A-Z]/.test(s) },
    { key: "lower", label: "A lowercase letter", met: /[a-z]/.test(s) },
    { key: "number", label: "A number", met: /[0-9]/.test(s) },
    { key: "special", label: "A special character", met: /[^A-Za-z0-9]/.test(s) },
    { key: "common", label: "Not a common or unsafe password", met: s.length > 0 && !isCommonPassword(s, email) },
  ];
}

export const allMet = (checks) => checks.every((c) => c.met);

/** Whether a new-password block is complete: every rule met and the confirmation matching. */
export function passwordReady(password, confirm, email) {
  return allMet(passwordChecks(password, { email })) && confirm === password;
}
